/** 关卡得分（0–5）的星级、分档与成长色。刻度未由后端下发，超出则夹到满分。 */

export const SCORE_MAX = 5

export const SCORE_TIER_COLOR = {
  low: '#6f9fff',
  mid: '#34d399',
  high: '#5eead4',
  perfect: '#f1b85b',
  none: '#9dafc6',
}

export function clampScore(score) {
  const n = Number(score)
  if (!Number.isFinite(n)) return null
  return Math.min(SCORE_MAX, Math.max(0, n))
}

export function formatScore(score) {
  const n = clampScore(score)
  if (n == null) return ''
  const rounded = Math.round(n * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

/** 五颗星的填充比例，每项为 0、0.5 或 1 */
export function scoreToStars(score) {
  const n = clampScore(score)
  if (n == null) return [0, 0, 0, 0, 0]
  return Array.from({ length: SCORE_MAX }, (_, index) => {
    const remain = n - index
    if (remain >= 0.75) return 1
    if (remain >= 0.25) return 0.5
    return 0
  })
}

export function scoreTier(score) {
  const n = clampScore(score)
  if (n == null) return 'none'
  if (n >= SCORE_MAX - 0.05) return 'perfect'
  if (n >= 3.5) return 'high'
  if (n >= 2) return 'mid'
  return 'low'
}

function lerp(a, b, t) {
  return Math.round(a + (b - a) * t)
}

/** 0 分偏冷蓝，2.5 分绿，满分偏暖金 */
export function scoreColor(score) {
  const n = clampScore(score)
  if (n == null) return SCORE_TIER_COLOR.none
  const stops = [
    { t: 0, c: [111, 159, 255] },
    { t: 0.5, c: [52, 211, 153] },
    { t: 1, c: [241, 184, 91] },
  ]
  const t = n / SCORE_MAX
  let left = stops[0]
  let right = stops[stops.length - 1]
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (t >= stops[i].t && t <= stops[i + 1].t) {
      left = stops[i]
      right = stops[i + 1]
      break
    }
  }
  const span = right.t - left.t || 1
  const local = (t - left.t) / span
  const [r, g, b] = [0, 1, 2].map((channel) => lerp(left.c[channel], right.c[channel], local))
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * 已评价关卡沿路径的渐变色标。offset 为 0–1，按节点纵向位置。
 * @param {Array<{ y: number, score: number }>} points
 */
export function growthStops(points, height) {
  const h = Math.max(Number(height) || 0, 1)
  const usable = (points || [])
    .map((point) => ({ y: Number(point.y) || 0, score: clampScore(point.score) }))
    .filter((point) => point.score != null)
    .sort((a, b) => a.y - b.y)
  if (!usable.length) return []

  const stops = []
  usable.forEach((point) => {
    const offset = Math.min(1, Math.max(0, point.y / h))
    const color = scoreColor(point.score)
    const prev = stops[stops.length - 1]
    if (prev && Math.abs(prev.offset - offset) < 0.001) {
      prev.color = color
      return
    }
    stops.push({ offset, color })
  })
  if (stops[0].offset > 0) stops.unshift({ offset: 0, color: stops[0].color })
  const last = stops[stops.length - 1]
  if (last.offset < 1) stops.push({ offset: 1, color: last.color })
  return stops
}

export function seenScoreStorageKey(courseId) {
  return `ybg.questMap.seenScores.${courseId || 'none'}`
}

/** 分差达到该值视为“大幅上升”，用于彩带 */
export const DELTA_BURST = 0.8

export function shouldBurst(score, delta) {
  const n = clampScore(score)
  if (n == null) return false
  if (n >= SCORE_MAX - 0.05) return true
  const d = Number(delta)
  return Number.isFinite(d) && d >= DELTA_BURST
}
